// Dated pay arrangements: "Commission until 30 Sep, Lease from 1 Oct".
//
// A driver's driver type used to apply to a whole pay week. These rows say "from this
// date the driver is on this driver type", so the payout can price each JOB by the
// arrangement in force on the job's date (src/lib/paymentRates.ts does the pricing).
// Drivers with no rows behave exactly as they always have.
import { supabase } from "@/lib/supabase";

export interface DriverTypeRule {
  id: string;
  type_key: string | null;
  type_name: string | null;
  is_leased: boolean | null;
  uses_bonus_tiers: boolean | null;
  uses_payment_type_percentages: boolean | null;
}

export interface PayArrangement {
  id: string;
  driver_name: string;
  /** ISO date (YYYY-MM-DD) the arrangement starts on. */
  effective_from: string;
  driver_type_id: string;
  note: string | null;
}

const isoDay = (d: string) => d.slice(0, 10);

/** Sort oldest first -- the order arrangementOn expects. */
export const sortArrangements = (list: PayArrangement[]): PayArrangement[] =>
  [...list].sort((a, b) => isoDay(a.effective_from).localeCompare(isoDay(b.effective_from)));

/**
 * The arrangement in force on an ISO date: the latest one that has started by then.
 * Null when the driver has no history, or the date is before their first entry
 * (callers then fall back to the old single-type behaviour).
 */
export function arrangementOn(sorted: PayArrangement[] | undefined, date: string | null | undefined): PayArrangement | null {
  if (!sorted || sorted.length === 0 || !date) return null;
  const day = isoDay(date);
  let found: PayArrangement | null = null;
  for (const a of sorted) {
    if (isoDay(a.effective_from) <= day) found = a;
    else break;
  }
  return found;
}

export async function loadDriverTypeRules(): Promise<Record<string, DriverTypeRule>> {
  const { data, error } = await supabase
    .from("driver_types")
    .select("id, type_key, type_name, is_leased, uses_bonus_tiers, uses_payment_type_percentages");
  if (error) {
    console.error("loadDriverTypeRules error:", error);
    return {};
  }
  return Object.fromEntries(((data as DriverTypeRule[]) || []).map((t) => [t.id, t]));
}

export async function loadPayArrangements(driverNames: string[]): Promise<Record<string, PayArrangement[]>> {
  const map: Record<string, PayArrangement[]> = {};
  if (!driverNames.length) return map;
  const { data, error } = await supabase
    .from("driver_pay_arrangements")
    .select("id, driver_name, effective_from, driver_type_id, note")
    .in("driver_name", driverNames)
    .order("effective_from", { ascending: true });
  if (error) {
    console.error("loadPayArrangements error:", error);
    return map;
  }
  (data as PayArrangement[] | null)?.forEach((r) => {
    (map[r.driver_name] ||= []).push(r);
  });
  Object.keys(map).forEach((k) => (map[k] = sortArrangements(map[k])));
  return map;
}

export async function savePayArrangement(args: {
  driverName: string;
  effectiveFrom: string;
  driverTypeId: string;
  note?: string;
}): Promise<void> {
  const { error } = await supabase.rpc("save_pay_arrangement", {
    p_driver_name: args.driverName,
    p_effective_from: args.effectiveFrom,
    p_driver_type_id: args.driverTypeId,
    p_note: args.note ?? null,
  });
  if (error) throw error;
}

export async function deletePayArrangement(id: string): Promise<void> {
  const { error } = await supabase.rpc("delete_pay_arrangement", { p_id: id });
  if (error) throw error;
}
